import { reconcileGridExportLimit } from "./gridExportLimit";
import {
  normalizeProjectMerchantRevenueProjection,
  prepareProjectMerchantRevenueProjectionForPersistence,
} from "./merchantRevenueProjection";
import {
  normalizeProjectTollingFloorUpside,
  prepareProjectTollingFloorUpsideForPersistence,
} from "./tollingFloorUpside";
import {
  normalizeProjectTollingFixedCapacity,
  prepareProjectTollingFixedCapacityForPersistence,
} from "./tollingFixedCapacity";
import {
  normalizeProjectHybridPartialTolling,
  prepareProjectHybridPartialTollingForPersistence,
} from "./hybridPartialTolling";
import {
  normalizeProjectDebtConfig,
  prepareProjectDebtConfigForPersistence,
} from "./projectDebt";
import { normalizeBtm } from "./btm";
import { repairDemandSchedule } from "./demandProjection";
import { normalizeProjectYears } from "../../utils/simulationYears";

export const AC_WIRING_MATERIAL_OPTIONS = ["Cu", "Al"];
export const AC_INSTALLATION_TYPE_OPTIONS = ["air", "buried", "conduit"];
export const DC_WIRING_MATERIAL_OPTIONS = ["Cu", "Al"];

export const DEFAULT_AC_MAIN_WIRING = {
  enabled: true,
  lengthM: "",
  autoSize: true,
  material: "Cu",
  capPercent: 100,
  powerFactor: 0.98,
  parallelRuns: 1,
  cableSectionMm2: null,
  installationType: "air",
  conductorTemperatureC: 60,
};

export const DEFAULT_AC_BRANCH_WIRING = {
  lengthM: "",
  autoSize: true,
  material: "Cu",
  capPercent: 100,
  powerFactor: 0.98,
  parallelRuns: 1,
  cableSectionMm2: null,
  installationType: "air",
  conductorTemperatureC: 60,
};

export const DEFAULT_DC_MPPT_WIRING = {
  panelLinkLengthM: "",
  panelCableSectionMm2: null,
  stringHomerunLengthM: "",
  homerunCableSectionMm2: null,
};

export const DEFAULT_DC_BRANCH_WIRING = {
  autoSize: true,
  material: "Cu",
  capPercent: 2,
};

export const DEFAULT_PV_LOSSES = {
  ac: {
    // New projects start with explicit, editable wiring assumptions. Dynamic
    // cable losses need measured lengths and must be selected deliberately.
    wiringMode: "static",
    wiringDynamic: {
      main: { ...DEFAULT_AC_MAIN_WIRING },
    },
    voltageDropPercent: 2,
    wiringStaticPercent: 0.5,
  },
  dc: {
    age: 0,
    lid: 1,
    snow: 0,
    shadingMode: "geometry",
    // Was 1.5 as a stand-in for row shading. That is now computed hour by hour
    // from the layout (see pv_core/row_shading.py), so anything left here would
    // double-count it. Covers only what the model cannot see: nearby objects.
    shading: 0,
    soiling: 2,
    mismatch: 2,
    wiringMode: "static",
    connections: 0.5,
    availability: 0,
    wiringDynamic: {},
    nameplate_rating: 0.5,
    voltageDropPercent: 1,
    wiringStaticPercent: 1.5,
  },
};

const MPPT_KEY_RE = /^MPPT(\d+)$/i;

function sortMpptKeys(keys) {
  return [...keys].sort((a, b) => {
    const aIdx = Number(String(a).match(MPPT_KEY_RE)?.[1] || 0);
    const bIdx = Number(String(b).match(MPPT_KEY_RE)?.[1] || 0);
    return aIdx - bIdx;
  });
}

function getSystemDesignInverterKeys(systemDesign) {
  if (!systemDesign || typeof systemDesign !== "object") return [];
  return Object.keys(systemDesign);
}

function getRawInverterKeys(rawPvLosses) {
  const acKeys = Object.keys(rawPvLosses?.ac?.wiringDynamic || {}).filter(
    (key) => key !== "main"
  );
  const dcKeys = Object.keys(rawPvLosses?.dc?.wiringDynamic || {});
  return [...new Set([...acKeys, ...dcKeys])];
}

function getInverterKeys(config, rawPvLosses) {
  return [
    ...new Set([
      ...getSystemDesignInverterKeys(config?.systemDesign),
      ...getRawInverterKeys(rawPvLosses),
    ]),
  ];
}

function getMpptKeysForInverter(config, rawPvLosses, inverterKey) {
  const systemMppts = Array.isArray(config?.systemDesign?.[inverterKey])
    ? config.systemDesign[inverterKey].map((_, idx) => `MPPT${idx + 1}`)
    : [];
  const rawMppts = Object.keys(rawPvLosses?.dc?.wiringDynamic?.[inverterKey] || {}).filter(
    (key) => MPPT_KEY_RE.test(key)
  );
  return sortMpptKeys([...new Set([...systemMppts, ...rawMppts])]);
}

function clone(value) {
  return JSON.parse(JSON.stringify(value));
}

export function normalizePvLosses(config) {
  const rawPvLosses = config?.pvLosses || {};
  const hasDegradationCurve = Array.isArray(config?.pvDegradation?.retentionPct) &&
    config.pvDegradation.retentionPct.length > 0;
  const inverterKeys = getInverterKeys(config, rawPvLosses);

  const normalizedAcDynamic = {
    main: {
      ...DEFAULT_AC_MAIN_WIRING,
      ...(rawPvLosses?.ac?.wiringDynamic?.main || {}),
    },
  };

  inverterKeys.forEach((inverterKey) => {
    normalizedAcDynamic[inverterKey] = {
      ...DEFAULT_AC_BRANCH_WIRING,
      ...(rawPvLosses?.ac?.wiringDynamic?.[inverterKey] || {}),
    };
  });

  const normalizedDcDynamic = {};
  inverterKeys.forEach((inverterKey) => {
    const rawBranch = rawPvLosses?.dc?.wiringDynamic?.[inverterKey] || {};
    const mpptKeys = getMpptKeysForInverter(config, rawPvLosses, inverterKey);

    normalizedDcDynamic[inverterKey] = {
      ...DEFAULT_DC_BRANCH_WIRING,
      autoSize:
        rawBranch.autoSize ?? DEFAULT_DC_BRANCH_WIRING.autoSize,
      material: rawBranch.material ?? DEFAULT_DC_BRANCH_WIRING.material,
      capPercent: rawBranch.capPercent ?? DEFAULT_DC_BRANCH_WIRING.capPercent,
    };

    mpptKeys.forEach((mpptKey) => {
      normalizedDcDynamic[inverterKey][mpptKey] = {
        ...DEFAULT_DC_MPPT_WIRING,
        ...(rawBranch[mpptKey] || {}),
      };
    });
  });

  return {
    ...clone(DEFAULT_PV_LOSSES),
    ...rawPvLosses,
    ac: {
      ...clone(DEFAULT_PV_LOSSES.ac),
      ...(rawPvLosses.ac || {}),
      wiringDynamic: normalizedAcDynamic,
      wiringMode:
        rawPvLosses?.ac?.wiringMode === "static" ? "static" : "dynamic",
      wiringStaticPercent:
        rawPvLosses?.ac?.wiringStaticPercent ?? DEFAULT_PV_LOSSES.ac.wiringStaticPercent,
    },
    dc: {
      ...clone(DEFAULT_PV_LOSSES.dc),
      ...(rawPvLosses.dc || {}),
      // The curve already accounts for module ageing from its first year.
      // A separate PVWatts age derate would count that deterioration twice.
      age: hasDegradationCurve ? 0 : (rawPvLosses?.dc?.age ?? DEFAULT_PV_LOSSES.dc.age),
      wiringDynamic: normalizedDcDynamic,
      wiringMode:
        rawPvLosses?.dc?.wiringMode === "static" ? "static" : "dynamic",
      wiringStaticPercent:
        rawPvLosses?.dc?.wiringStaticPercent ?? DEFAULT_PV_LOSSES.dc.wiringStaticPercent,
    },
  };
}

export function normalizeProjectPvLosses(config) {
  let tollingNormalizedConfig = normalizeProjectTollingFloorUpside(
    normalizeProjectTollingFixedCapacity(normalizeProjectHybridPartialTolling(config))
  );
  if (config?.settlementType === "TollingFixed") {
    tollingNormalizedConfig = normalizeProjectTollingFixedCapacity(tollingNormalizedConfig);
  } else if (config?.settlementType === "HybridPartial") {
    tollingNormalizedConfig = normalizeProjectHybridPartialTolling(tollingNormalizedConfig);
  } else if (config?.settlementType === "TollingFloorUpside") {
    tollingNormalizedConfig = normalizeProjectTollingFloorUpside(tollingNormalizedConfig);
  }
  const normalizedConfig = normalizeProjectYears(normalizeProjectDebtConfig(
    normalizeProjectMerchantRevenueProjection(tollingNormalizedConfig)
  ));
  return {
    ...normalizedConfig,
    // Behind-the-Meter terms are normalized for every project (the normalizer is
    // pure and its defaults are inert), so a BTM block is always well-shaped
    // regardless of which revenue model is currently selected.
    btm: normalizeBtm(normalizedConfig),
    // Fill in workday-schedule entries that were never persisted (the selects
    // used to show an hour while storing an empty string, which saved a site
    // open zero hours a week). Explicit "None" values are left untouched.
    feasibility: {
      ...(normalizedConfig.feasibility || {}),
      technical: {
        ...((normalizedConfig.feasibility || {}).technical || {}),
        demand: {
          ...(((normalizedConfig.feasibility || {}).technical || {}).demand || {}),
          schedule: normalizedConfig.feasibility?.technical?.demand?.shape?.version === 1
            ? (normalizedConfig.feasibility.technical.demand.schedule || {})
            : repairDemandSchedule(
              (((normalizedConfig.feasibility || {}).technical || {}).demand || {}).schedule
            ).schedule,
        },
      },
    },
    pvLosses: normalizePvLosses(normalizedConfig),
  };
}

export function pruneInactivePvLosses(pvLosses) {
  const normalized = normalizePvLosses({ pvLosses });
  const next = clone(normalized);

  if (next.ac?.wiringMode === "static") {
    delete next.ac.wiringDynamic;
  } else {
    delete next.ac.wiringStaticPercent;
  }

  if (next.dc?.wiringMode === "static") {
    delete next.dc.wiringDynamic;
  } else {
    delete next.dc.wiringStaticPercent;
  }

  return next;
}

export function prepareProjectConfigForPersistence(config) {
  const normalized = normalizeProjectPvLosses(reconcileGridExportLimit(config));
  const baseConfig = {
    ...normalized,
    pvLosses: pruneInactivePvLosses(normalized.pvLosses),
  };
  let tollingPreparedConfig = prepareProjectTollingFloorUpsideForPersistence(
    prepareProjectTollingFixedCapacityForPersistence(
      prepareProjectHybridPartialTollingForPersistence(baseConfig)
    )
  );
  if (normalized?.settlementType === "TollingFixed") {
    tollingPreparedConfig = prepareProjectTollingFixedCapacityForPersistence(tollingPreparedConfig);
  } else if (normalized?.settlementType === "HybridPartial") {
    tollingPreparedConfig = prepareProjectHybridPartialTollingForPersistence(tollingPreparedConfig);
  } else if (normalized?.settlementType === "TollingFloorUpside") {
    tollingPreparedConfig = prepareProjectTollingFloorUpsideForPersistence(tollingPreparedConfig);
  }
  return prepareProjectDebtConfigForPersistence(
    prepareProjectMerchantRevenueProjectionForPersistence(tollingPreparedConfig)
  );
}
